import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ================= FETCH ALL ORDERS (ADMIN) ================= */
export const fetchOrders = createAsyncThunk(
  "orders/fetchOrders",
  async ({ page = 1, limit = 10, status = "", deliveryMode = "" } = {}, thunkAPI) => {
    try {
      const res = await api.get(
        `/api/orders?page=${page}&limit=${limit}&status=${status}&deliveryMode=${deliveryMode}`
      );
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch orders");
    }
  }
);

/* ================= FETCH STATUS COUNTS (ADMIN) ================= */
export const fetchStatusCounts = createAsyncThunk(
  "orders/fetchStatusCounts",
  async ({ deliveryMode = "delivery", statuses = [] } = {}, thunkAPI) => {
    try {
      const results = await Promise.all(
        statuses.map((status) =>
          api
            .get(`/api/orders?page=1&limit=1&status=${status}&deliveryMode=${deliveryMode}`)
            .then((res) => ({ status, total: res.data.pagination?.total ?? 0 }))
            .catch(() => ({ status, total: 0 }))
        )
      );
      return results.reduce((acc, { status, total }) => {
        acc[status] = total;
        return acc;
      }, {});
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch status counts");
    }
  }
);

/* ================= FETCH SINGLE ORDER (ADMIN) ================= */
export const fetchOrderDetails = createAsyncThunk(
  "orders/fetchOrderDetails",
  async (id, thunkAPI) => {
    try {
      const res = await api.get(`/api/orders/${id}`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to fetch order details"
      );
    }
  }
);

/* ================= UPDATE ORDER STATUS (ADMIN) ================= */
export const updateOrderStatus = createAsyncThunk(
  "orders/updateOrderStatus",
  async ({ orderId, orderStatus, estimatedDeliveryTime, estimatedDuration }, thunkAPI) => {
    try {
      // ✅ Build body — only include estimate fields if provided
      const body = { orderStatus };
      if (estimatedDeliveryTime) body.estimatedDeliveryTime = estimatedDeliveryTime;
      if (estimatedDuration)     body.estimatedDuration     = Number(estimatedDuration);

      const res = await api.patch(`/api/orders/${orderId}/status`, body);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to update order status"
      );
    }
  }
);

/* ================= FETCH MY ORDERS (USER) ================= */
export const fetchMyOrders = createAsyncThunk(
  "orders/fetchMyOrders",
  async (_, thunkAPI) => {
    try {
      const res = await api.get(`/api/orders/my-orders`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to fetch user orders"
      );
    }
  }
);

/* ================= FETCH CUSTOMER ORDERS (ADMIN) ================= */
export const fetchCustomerOrders = createAsyncThunk(
  "orders/fetchCustomerOrders",
  async (customerId, thunkAPI) => {
    try {
      const res = await api.get(`/api/orders/customer/${customerId}`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to fetch customer orders"
      );
    }
  }
);

/* ================= SLICE ================= */
const orderSlice = createSlice({
  name: "orders",
  initialState: {
    orders:       [],
    myOrders:     [],
     customerOrders:  [], 
    orderDetails: null,
    loading:      false,
    updating:     false,
    error:        null,
    pagination:   {},
    statusCounts: {
      delivery: {},
      pickup:   {},
    },
  },
  reducers: {
    clearOrderError:   (state) => { state.error = null; },
    clearOrderDetails: (state) => { state.orderDetails = null; },
  },
  extraReducers: (builder) => {
    builder

      /* ── FETCH ORDERS ── */
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading    = false;
        state.orders     = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      })

      /* ── FETCH STATUS COUNTS ── */
      .addCase(fetchStatusCounts.fulfilled, (state, action) => {
        const mode = action.meta.arg?.deliveryMode || "delivery";
        state.statusCounts[mode] = action.payload;
      })

      /* ── FETCH ORDER DETAILS ── */
      .addCase(fetchOrderDetails.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.loading      = false;
        state.orderDetails = action.payload;
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      })

      /* ── UPDATE ORDER STATUS ── */
      .addCase(updateOrderStatus.pending, (state) => {
        state.updating = true;
        state.error    = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.updating = false;
        const updatedOrder = action.payload;
        if (updatedOrder?._id) {
          state.orders = state.orders.filter((o) => o._id !== updatedOrder._id);
          if (state.pagination?.total) {
            state.pagination.total -= 1;
          }
        }
        if (state.orderDetails?._id === updatedOrder?._id) {
          state.orderDetails = updatedOrder;
        }
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.updating = false;
        state.error    = action.payload;
      })

      /* ── FETCH MY ORDERS ── */
      .addCase(fetchMyOrders.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchMyOrders.fulfilled, (state, action) => {
        state.loading  = false;
        state.myOrders = action.payload;
      })
      .addCase(fetchMyOrders.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      })
        .addCase(fetchCustomerOrders.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchCustomerOrders.fulfilled, (state, action) => {
        state.loading        = false;
        state.customerOrders = action.payload;
      })
      .addCase(fetchCustomerOrders.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      });
  },
});

export const { clearOrderError, clearOrderDetails } = orderSlice.actions;
export default orderSlice.reducer;

/* ================= SELECTORS ================= */
export const selectAllOrders     = (state) => state.orders.orders       || [];
export const selectMyOrders      = (state) => state.orders.myOrders     || [];
export const selectOrderDetails  = (state) => state.orders.orderDetails;
export const selectPagination    = (state) => state.orders.pagination   || {};
export const selectOrderLoading  = (state) => state.orders.loading;
export const selectOrderUpdating = (state) => state.orders.updating;
export const selectOrderError    = (state) => state.orders.error;
export const selectStatusCounts  = (state) => state.orders.statusCounts || {};
export const selectCustomerOrders = (state) => state.orders.customerOrders || [];